import { useCallback, useMemo, useState } from 'react';
import type { InfiniteData, QueryKey } from 'react-query';
import { useInfiniteQuery } from 'react-query';
import type { TransactionFacets } from '@context/ReportsContext/types';
import { useQueryString } from '@goodfynd/hooks';
import type { FilterMenuOption } from '@goodfynd/ui';
import findLast from 'lodash/findLast';
import { useRouter } from 'next/router';
import type { FacetGroup } from 'types/search';
import type {
  TransactionSearchFacetFields,
  TransactionSearchResults,
} from 'types/transactions';

import config from '../config';
import { useStores } from '../stores';
import type { OrderSearchRequest } from '../types/orders';

import { useOrderApi } from './api';
type Request = Omit<OrderSearchRequest, 'dates' | 'sorts' | 'typeList'>;

type OrderSearchOptions = {
  defaultRows?: number;
  enabled?: boolean;
  getDefaultRequest?: (hasSearchTerm?: boolean) => OrderSearchRequest;
  onSuccess?:
    | ((data: InfiniteData<TransactionSearchResults>) => void)
    | undefined;
  organizationId?: string;
  queryKey?: QueryKey;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
  request?: Request;
  updateSearch?: () => void;
};

export enum OrderSort {
  PickupTimeAsc = '1',
  PickupTimeDesc = '2',
  TimeCreatedAsc = '3',
  TimeCreatedDesc = '4',
  DueDateAsc = '5',
  DueDateDesc = '6',
  TimeCompletedAsc = '7',
  TimeCompletedDesc = '8',
}
interface Stats {
  facets: TransactionFacets;
  page: number;
}

export function useOrderSearch({
  defaultRows = 6,
  enabled = true,
  getDefaultRequest,
  onSuccess,
  organizationId,
  queryKey = config.queryKeys.orderSearch,
  refetchInterval,
  refetchOnWindowFocus = false,
  updateSearch,
  ...options
}: OrderSearchOptions) {
  const api = useOrderApi();
  const router = useRouter();
  const [, updateQueryString] = useQueryString(updateSearch);
  const {
    orderStore: { search },
  } = useStores();

  const [rows, setRowsPerPage] = useState(
    Number(router.query.rows || defaultRows)
  );

  const [busy, toggleBusy] = useState(false);
  const [{ facets, page }, setStats] = useState<Stats>(() => {
    return {
      facets: {
        lots: [],
        merchants: [],
        organizations: [],
        paymentStatuses: [],
        postalCodes: [],
        steps: [],
        territories: [],
        types: [],
      },
      page: 1,
    };
  });

  const bindFacet = useCallback(
    (
      key: TransactionSearchFacetFields,
      facet?: Record<
        TransactionSearchFacetFields,
        FacetGroup<TransactionSearchFacetFields>
      >
    ) => {
      return (facet?.[key]?.buckets || [])
        .map((bucket) => {
          const parts = bucket.id.split('|');
          const option: FilterMenuOption = {
            count: bucket.count,
            label: parts?.[2] || parts?.[1] || bucket.id,
            nameId: parts?.[2] && parts[1],
            value: parts?.[0] || bucket.id,
          };
          return option;
        })
        .sort((a, b) => (a.label < b.label ? 0 : 1));
    },
    []
  );

  const request = useMemo<OrderSearchOptions['request'] | undefined>(() => {
    if (!options.request) {
      return;
    }

    let newRequest = {
      query:
        typeof options.request?.query === 'string'
          ? options.request.query
          : search?.term,

      ...options.request,
    };

    if (newRequest.dateFilterType) {
      newRequest = {
        ...newRequest,
        [`${newRequest.dateFilterType}Start`]: newRequest?.dateStart,
        [`${newRequest.dateFilterType}End`]: newRequest?.dateEnd,
      };

      delete newRequest['dateFilterType'];
      delete newRequest['dateEnd'];
      delete newRequest['dateStart'];
    }

    return {
      rows,
      ...newRequest,
    };
  }, [options.request, rows, search?.term]);

  const finalQueryKey = useMemo(
    () => [queryKey, request, router.asPath?.split('?')[0]],
    [queryKey, request, router.asPath]
  );

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    isFetched,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery<TransactionSearchResults, unknown>(
    finalQueryKey,
    async ({ pageParam = 1 }) => {
      const searchTerm = search?.term || options.request?.query;
      const requestParams = {
        rows: 100,
        ...request,
        ...getDefaultRequest?.(typeof searchTerm === 'string' && !!searchTerm),
        page: pageParam,
      };
      if (!requestParams.organizationIds) {
        requestParams.organizationIds = organizationId || '';
      }

      return await api.searchLegacy(requestParams);
    },
    {
      enabled: enabled && !!request && router.isReady,
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.page + 1 : undefined,
      onSuccess: (data) => {
        try {
          toggleBusy(true);
          onSuccess?.(data);

          const pageData = data.pages.at(-1);
          if (!pageData) {
            return;
          }

          setStats((oldStats) => ({
            ...oldStats,
            facets: {
              lots: bindFacet('lot', pageData.metadata?.facet),
              merchants: bindFacet('merchant', pageData.metadata?.facet),
              organizations: bindFacet(
                'organization',
                pageData.metadata?.facet
              ),
              paymentStatuses: bindFacet(
                'paymentStatus',
                pageData.metadata?.facet
              ),
              postalCodes: bindFacet('postalCode', pageData.metadata?.facet),
              steps: bindFacet('step', pageData.metadata?.facet),
              territories: bindFacet('territory', pageData.metadata?.facet),
              types: bindFacet('type', pageData.metadata?.facet),
            },
            page: pageData.page,
            sum: 0,
            total: pageData.total,
          }));

          if (request && updateSearch) {
            updateQueryString(request);
          }
        } finally {
          toggleBusy(false);
        }
      },
      refetchInterval,
      refetchOnWindowFocus,
    }
  );

  const currentPageData = useMemo(() => {
    const lastPage = findLast(data?.pages, (row) => row.page === page);
    return lastPage;
  }, [data, page]);

  const { total, sum } = useMemo(
    () => ({
      sum: 0,
      total: currentPageData?.total || 0,
    }),
    [currentPageData?.total]
  );

  return {
    facets,
    fetchNextPage,
    fetchPreviousPage,
    finalQueryKey,
    hasNextPage,
    isFetched,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || (busy && total === 0),
    list: useMemo(
      () => currentPageData?.results || [],
      [currentPageData?.results]
    ),
    page,
    pages: Math.ceil(total / rows),
    refetch,
    results: useMemo(
      () => data?.pages?.flatMap((page) => page.results) || [],
      [data]
    ),
    search: {
      ...search,
      rowsPerPage: rows,
      setRowsPerPage,
    },
    setRowsPerPage,
    sum,
    total,
  };
}
